/**
 * Agrupación: traducir lo que devuelve `read_group` a algo que se pueda mostrar
 * y volver a filtrar.
 *
 * Dos cosas que el backend no puede resolver por nosotros:
 *
 * 1. Al agrupar por un many2one, la clave del grupo es el **id** (el backend
 *    agrupa por `F(campo)`), no el nombre. Hay que resolverlo con una consulta
 *    aparte, y por eso está el hook {@link useGroupLabels}.
 * 2. Al agrupar por fecha con granularidad (`fecha:month`), la clave es el
 *    comienzo del período. Para volver a pedir las filas de ese grupo no sirve
 *    la igualdad: hace falta un rango `[inicio, siguiente)`.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { choiceLabel, LOCALE } from '@/components/fields/format';
import { api } from '@/lib/api';
import type { Domain, FieldMeta, JsonValue, ModelSpec } from '@/lib/types';
import { useCompanyId } from '@/views/queries';

/** `"fecha:month"` -> `{ campo: "fecha", granularidad: "month" }`. */
export function splitGroupBy(spec: string): { campo: string; granularidad: string | null } {
  const posicion = spec.indexOf(':');
  if (posicion < 0) return { campo: spec, granularidad: null };
  return { campo: spec.slice(0, posicion), granularidad: spec.slice(posicion + 1) };
}

/** Clave estable para React y para los mapas. `null` es un grupo legítimo. */
export function groupKey(valor: JsonValue): string {
  return valor === null || valor === undefined ? '__vacio__' : String(valor);
}

// ---------------------------------------------------------------------------
// Fechas: leerlas sin que se corran de mes
// ---------------------------------------------------------------------------
/**
 * El comienzo de un período tal como lo manda `read_group`, a `Date` **local**.
 *
 * `new Date("2026-08-01")` es medianoche UTC. Formatear eso en Buenos Aires
 * (UTC−3) devuelve el 31 de julio a las 21:00, y el rótulo del grupo decía
 * «julio de 2026» sobre nueve facturas del 6 de agosto. Todos los meses salían
 * corridos uno para atrás y el mes en curso no figuraba en la lista.
 *
 * Una clave de grupo por mes, por trimestre o por año es una **fecha de
 * calendario**, no un instante: el 1 de agosto es el 1 de agosto acá y en
 * Tokio. Se arma a medianoche local, que es el mismo criterio que ya usan
 * `formatDate` y `daysUntil` en `components/fields/format.ts`.
 *
 * Una clave con hora (`2026-08-06T03:00:00Z`) sí es un instante y se deja que
 * `Date` la interprete: ahí la zona de quien mira es justamente el dato.
 */
function fechaDeGrupo(valor: string): Date | null {
  const soloFecha = valor.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (soloFecha !== null) {
    const anio = Number.parseInt(soloFecha[1] as string, 10);
    const mes = Number.parseInt(soloFecha[2] as string, 10);
    const dia = Number.parseInt(soloFecha[3] as string, 10);
    return new Date(anio, mes - 1, dia);
  }
  const instante = new Date(valor);
  return Number.isNaN(instante.getTime()) ? null : instante;
}

/** `Date` local a ISO `AAAA-MM-DD`, sin pasar por UTC (que la correría un día). */
function isoLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

// ---------------------------------------------------------------------------
// Dominio de un grupo
// ---------------------------------------------------------------------------
function sumarUnidad(inicio: Date, granularidad: string): Date {
  const fin = new Date(inicio.getTime());
  switch (granularidad) {
    case 'day':
      fin.setDate(fin.getDate() + 1);
      break;
    case 'week':
      fin.setDate(fin.getDate() + 7);
      break;
    case 'month':
      fin.setMonth(fin.getMonth() + 1);
      break;
    case 'quarter':
      fin.setMonth(fin.getMonth() + 3);
      break;
    case 'year':
      fin.setFullYear(fin.getFullYear() + 1);
      break;
    default:
      fin.setDate(fin.getDate() + 1);
  }
  return fin;
}

/**
 * Dominio que devuelve exactamente las filas de un grupo.
 *
 * Con granularidad se emite un rango en vez de una igualdad, porque el valor del
 * grupo es el comienzo del período y ninguna fila tiene ese valor exacto salvo
 * por casualidad.
 */
export function groupDomain(spec: string, valor: JsonValue, meta: FieldMeta | undefined): Domain {
  const { campo, granularidad } = splitGroupBy(spec);

  if (valor === null || valor === undefined) return [[campo, '=', null]];

  if (granularidad !== null && typeof valor === 'string') {
    const inicio = fechaDeGrupo(valor);
    if (inicio !== null) {
      const fin = sumarUnidad(inicio, granularidad);
      const esFecha = meta?.type === 'date';
      const aTexto = (fecha: Date): string => (esFecha ? isoLocal(fecha) : fecha.toISOString());
      return [
        [campo, '>=', aTexto(inicio)],
        [campo, '<', aTexto(fin)],
      ];
    }
  }

  return [[campo, '=', valor]];
}

// ---------------------------------------------------------------------------
// Etiquetas
// ---------------------------------------------------------------------------
/**
 * Lo que se lee en la cabecera de un grupo por fecha: «agosto de 2026».
 *
 * Exportada para que el corrimiento de mes tenga test propio: es un defecto que
 * no se ve mirando la pantalla salvo que uno compare grupo por grupo contra la
 * lista sin agrupar, que es como se encontró.
 */
export function etiquetaDeGrupo(valor: string, granularidad: string): string {
  const fecha = fechaDeGrupo(valor);
  if (fecha === null) return valor;
  if (granularidad === 'year') return String(fecha.getFullYear());
  if (granularidad === 'month') {
    return new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' }).format(fecha);
  }
  if (granularidad === 'quarter') {
    return `${Math.floor(fecha.getMonth() / 3) + 1}º trimestre ${fecha.getFullYear()}`;
  }
  return new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(fecha);
}

/**
 * Etiquetas visibles de los valores de un grupo.
 *
 * Devuelve una función porque el consumidor la llama por cada columna o cabecera
 * mientras dibuja; el resultado es sincrónico y, mientras los nombres de los
 * many2one no llegaron, muestra el id — que es feo pero no miente.
 */
export function useGroupLabels(
  spec: ModelSpec,
  groupBy: string | null,
  valores: JsonValue[],
): (valor: JsonValue) => string {
  const companyId = useCompanyId();
  const { campo, granularidad } = splitGroupBy(groupBy ?? '');
  const meta = groupBy === null ? undefined : spec.fields[campo];
  const relation = meta?.type === 'many2one' ? meta.relation : undefined;

  const ids = relation === undefined ? [] : valores.filter((valor): valor is number => typeof valor === 'number');

  const nombres = useQuery({
    queryKey: ['group-labels', companyId, relation ?? '', ids],
    queryFn: ({ signal }) => {
      const domain: Domain = [['id', 'in', ids]];
      // `include_inactive`: los nombres se piden por id, así que no hay riesgo
      // de traer de más —los ids son exactamente los grupos que el backend ya
      // devolvió—. Sin esto, un registro ARCHIVADO no venía en la respuesta y la
      // cabecera del grupo caía al `#id`: en Ventas, agrupar por «Cliente»
      // dibujaba un grupo llamado «#421» con $ 1.421.050 adentro. Archivar un
      // contacto que tiene documentos es lo correcto —no se puede borrar—, así
      // que el caso no es raro: sus ventas siguen contando en el total y su
      // grupo tiene que decir su nombre.
      return api.searchRead(
        relation as string,
        { domain, limit: ids.length, include_inactive: true },
        signal,
      );
    },
    enabled: relation !== undefined && ids.length > 0,
    staleTime: 60_000,
  });

  const mapa = new Map<number, string>(
    (nombres.data?.results ?? []).map((registro) => [registro.id, registro.display_name]),
  );

  return (valor: JsonValue): string => {
    if (valor === null || valor === undefined || valor === '') return 'Sin definir';
    if (meta === undefined) return String(valor);
    if (relation !== undefined && typeof valor === 'number') return mapa.get(valor) ?? `#${valor}`;
    if (meta.type === 'boolean') return valor === true || valor === 'true' ? 'Sí' : 'No';
    if (granularidad !== null && typeof valor === 'string') return etiquetaDeGrupo(valor, granularidad);
    if (meta.choices !== undefined && meta.choices.length > 0) return choiceLabel(meta, valor);
    return String(valor);
  };
}

// ---------------------------------------------------------------------------
// Esqueleto de columnas
// ---------------------------------------------------------------------------

/**
 * Tope de columnas que se traen de la relacion al agrupar por many2one.
 *
 * Existe porque «expandir la relacion» es correcto para una tabla de etapas
 * (`crm.stage` tiene seis filas) y absurdo para una de negocio: agrupar variantes
 * por producto dibujaria una columna vacia por cada producto del catalogo. Si la
 * relacion no entra en el tope, no se expande y el tablero vuelve a mostrar solo
 * lo que tiene tarjetas — que es peor, pero no es peor QUE ESTO.
 */
const TOPE_COLUMNAS = 40;

/**
 * Valores que el tablero tiene que dibujar SIEMPRE, tenga tarjetas o no.
 *
 * `read_group` devuelve unicamente los grupos con filas, asi que un tablero
 * armado solo con su respuesta no tiene columna para los estados vacios. En un
 * embudo eso no es una imperfeccion visual: la columna que falta es la que no se
 * puede elegir. Con las tres oportunidades de la demo repartidas en Calificado,
 * Propuesta enviada y Negociacion, «Ganada» y «Perdida» no existian en el
 * tablero ni en el selector «Mover a» — o sea que no habia forma de cerrar una
 * venta desde el embudo. Y un modelo sin ningun registro no dibujaba ninguna
 * columna.
 *
 * De donde sale el conjunto completo, por orden de preferencia:
 *
 * 1. `choices` del campo: es el caso de los quince tableros de estado
 *    (`sales.order`, `invoicing.invoice`, `inventory.picking`…). Viene en la
 *    metadata, en el orden que declaro el modulo, y no cuesta una peticion.
 * 2. `boolean`: los dos valores.
 * 3. `many2one`: se lee la relacion, acotada por {@link TOPE_COLUMNAS}.
 *
 * Devuelve `null` cuando el conjunto no se puede enumerar (agrupacion por fecha,
 * texto libre, relacion demasiado grande). Ahi el consumidor se queda con lo que
 * dio `read_group`.
 */
export function useColumnSkeleton(
  spec: ModelSpec,
  groupBy: string | null,
): { valores: JsonValue[] | null; isPending: boolean } {
  const companyId = useCompanyId();
  const { campo, granularidad } = splitGroupBy(groupBy ?? '');
  const meta = groupBy === null ? undefined : spec.fields[campo];

  // Agrupar por mes no tiene un conjunto cerrado de columnas: los periodos que
  // existen son justamente los que tienen filas.
  const enumerable = groupBy !== null && granularidad === null && meta !== undefined;
  const relation = enumerable && meta.type === 'many2one' ? meta.relation : undefined;

  const relacionados = useQuery({
    queryKey: ['column-skeleton', companyId, relation ?? ''],
    queryFn: ({ signal }) =>
      // Se pide uno mas que el tope para distinguir «entra justo» de «hay mas».
      api.searchRead(relation as string, { limit: TOPE_COLUMNAS + 1, fields: ['id'] }, signal),
    enabled: relation !== undefined,
    staleTime: 60_000,
  });

  // Memoizado: sin esto `choices.map(...)` devuelve un array nuevo en cada
  // render y el consumidor no puede depender de su identidad —el mismo problema
  // que `useSearchState` documenta para el dominio.
  const valores = useMemo<JsonValue[] | null>(() => {
    if (!enumerable || meta === undefined) return null;
    if (meta.choices !== undefined && meta.choices.length > 0) {
      return meta.choices.map((opcion) => opcion.value);
    }
    if (meta.type === 'boolean') return [true, false];
    if (relation !== undefined) {
      if (relacionados.isPending) return null;
      const filas = relacionados.data?.results ?? [];
      if (filas.length > TOPE_COLUMNAS) return null;
      return filas.map((fila) => fila.id);
    }
    return null;
  }, [enumerable, meta, relation, relacionados.isPending, relacionados.data]);

  return { valores, isPending: relation !== undefined && relacionados.isPending };
}
